import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"

import { cn } from "@/lib/utils"
import { usePortalContainer } from "@/common/hooks/use-portal-container"
import { Z_LAYERS } from "./z-layers"

function Popover({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return (
    <PopoverPrimitive.Root data-slot="popover" {...props} />
  )
}

interface PopoverTriggerProps extends React.ComponentProps<typeof PopoverPrimitive.Trigger> {}

function PopoverTrigger({
  ...props
}: PopoverTriggerProps) {
  return (
    <PopoverPrimitive.Trigger
      data-slot="popover-trigger"
      {...props}
    />
  )
}

interface PopoverPortalProps extends React.ComponentProps<typeof PopoverPrimitive.Portal> {
  container?: HTMLElement | ShadowRoot; // Optional container override (auto-detected if not provided)
}

function PopoverPortal({
  container: containerProp,
  ...props
}: PopoverPortalProps) {
  // Automatically detect shadow root container if not explicitly provided
  const autoContainer = usePortalContainer();
  const container = containerProp || autoContainer;

  return (
    <PopoverPrimitive.Portal container={container} data-slot="popover-portal" {...props} />
  )
}

interface PopoverContentProps extends React.ComponentProps<typeof PopoverPrimitive.Content> {
  container?: HTMLElement | ShadowRoot; // Optional container override (auto-detected if not provided)
  zIndex?: number; // Optional z-index override (defaults to Z_LAYERS.popover: above dialogs)
}

function PopoverContent({
  className,
  align = "center",
  sideOffset = 4,
  container,
  zIndex = Z_LAYERS.popover, // above the dialog layer: a popover may open from inside a dialog
  ...props
}: PopoverContentProps) {
  // Automatically detect shadow root container if not explicitly provided
  const autoContainer = usePortalContainer();
  const portalContainer = container || autoContainer;

  // Merge styles ensuring backgroundColor is always set
  // Allow backgroundColor override from props.style if explicitly provided
  const mergedStyle = React.useMemo(() => {
    const defaultBackgroundColor = 'var(--color-surface, #ffffff)';
    
    // Check if backgroundColor is explicitly provided in props.style
    const hasExplicitBackground = props.style && typeof props.style === 'object' && !Array.isArray(props.style) &&
      ('backgroundColor' in props.style || 'background-color' in props.style);
    
    const backgroundColor = hasExplicitBackground && props.style && typeof props.style === 'object' && !Array.isArray(props.style)
      ? (props.style.backgroundColor || (props.style as Record<string, string>)['background-color'] || defaultBackgroundColor)
      : defaultBackgroundColor;

    // Filter out backgroundColor from props.style to prevent conflicts
    const filteredStyle = props.style && typeof props.style === 'object' && !Array.isArray(props.style)
      ? Object.fromEntries(
          Object.entries(props.style).filter(([key]) => 
            key !== 'backgroundColor' && key !== 'background-color'
          )
        )
      : props.style;

    return {
      backgroundColor: backgroundColor,
      color: 'var(--color-text, #111827)',
      borderColor: 'var(--color-border, #e5e7eb)',
      pointerEvents: 'auto' as const, // Ensure content is interactive even if portal container is pointer-events: none
      ...filteredStyle,
    };
  }, [props.style]);

  // Also set background color and z-index directly in ref callback to ensure they're applied
  const contentRefWithBackground = React.useCallback((node: HTMLDivElement | null) => {
    if (!node) return;

    // Ensure background color is set
    const defaultBackgroundColor = 'var(--color-surface, #ffffff)';
    const hasExplicitBackground = props.style && typeof props.style === 'object' && !Array.isArray(props.style) &&
      ('backgroundColor' in props.style || 'background-color' in props.style);
    
    const backgroundColor = hasExplicitBackground && props.style && typeof props.style === 'object' && !Array.isArray(props.style)
      ? (props.style.backgroundColor || (props.style as Record<string, string>)['background-color'] || defaultBackgroundColor)
      : defaultBackgroundColor;

    node.style.setProperty('background-color', backgroundColor, 'important');

    // Set z-index on content element - Radix Popper reads this and applies to wrapper
    node.style.setProperty('z-index', String(zIndex), 'important');
    
    // Also directly set on wrapper if it exists (Radix creates it synchronously in some cases)
    let element: HTMLElement | null = node;
    while (element && element.parentElement) {
      element = element.parentElement;
      if (element.hasAttribute('data-radix-popper-content-wrapper')) {
        element.style.setProperty('z-index', String(zIndex), 'important');
        break;
      }
    }

    // If wrapper doesn't exist yet, use one frame delay
    if (!element || !element.hasAttribute('data-radix-popper-content-wrapper')) {
      requestAnimationFrame(() => {
        let wrapper: HTMLElement | null = node;
        while (wrapper && wrapper.parentElement) {
          wrapper = wrapper.parentElement;
          if (wrapper.hasAttribute('data-radix-popper-content-wrapper')) {
            wrapper.style.setProperty('z-index', String(zIndex), 'important');
            break;
          }
        }
      });
    }

    // Forward ref if provided in props
    if (props.ref) {
      if (typeof props.ref === 'function') {
        props.ref(node);
      } else {
        (props.ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    }
  }, [props.ref, props.style, zIndex]);

  return (
    <PopoverPortal container={portalContainer}>
      <PopoverPrimitive.Content
        ref={contentRefWithBackground}
        data-slot="popover-content"
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "w-72 rounded-md border p-4 shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className
        )}
        style={mergedStyle}
        {...props}
      />
    </PopoverPortal>
  )
}

function PopoverAnchor({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return (
    <PopoverPrimitive.Anchor
      data-slot="popover-anchor"
      {...props}
    />
  )
}

export {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverAnchor,
}

